import React, { useEffect, useState } from "react";
import { X, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { timeAgo } from "../../lib/format";
import LogoUploader from "../../components/LogoUploader";

const TYPES = [
  { value: "ecom_x_brand",    label: "Ecom × Brand",    hint: "Two partners, both with logo (e.g. Flipkart × Garnier)" },
  { value: "ecom_x_category", label: "Ecom × Category", hint: "One partner + a category name (e.g. Flipkart × Beauty)" },
  { value: "brand_only",      label: "Brand only (D2C)", hint: "Single brand only, no partner (e.g. Garnier D2C)" },
];
const STATUSES = ["Pending", "Active", "Completed", "Expired"];
const VR_STATUSES = [
  { v: "not_generated", label: "Not generated" },
  { v: "in_progress", label: "In progress" },
  { v: "pending_review", label: "Pending review" },
  { v: "approved", label: "Approved" },
  { v: "shared_with_brand", label: "Shared with brand" },
];

export default function CampaignFormModal({ campaign, brands, teams, onClose, onSaved, onConfigure }) {
  const isEdit = !!campaign;
  const [trackerConfig, setTrackerConfig] = useState({ fields: [] });
  const [form, setForm] = useState(() => ({
    brand_id: campaign?.brand_id || (brands[0]?.id || ""),
    campaign_id: campaign?.campaign_id || "",
    campaign_name: campaign?.campaign_name || "",
    campaign_type: campaign?.campaign_type || "ecom_x_brand",
    partner_1_name: campaign?.partner_1_name || "",
    partner_1_logo_url: campaign?.partner_1_logo_url || "",
    partner_2_name: campaign?.partner_2_name || "",
    partner_2_logo_url: campaign?.partner_2_logo_url || "",
    category_name: campaign?.category_name || "",
    google_sheet_id: campaign?.google_sheet_id || "",
    sheet_tab_campaign: campaign?.sheet_tab_campaign || "campaign",
    sheet_tab_creators: campaign?.sheet_tab_creators || "creators",
    sheet_tab_content:  campaign?.sheet_tab_content  || "content",
    status: campaign?.status || "Pending",
    visible_to_brand: !!campaign?.visible_to_brand,
    expiry_date: campaign?.expiry_date || "",
    team_ids: campaign?.team_ids || [],
    partner_brand_ids: campaign?.partner_brand_ids || [],
    tracker_values: campaign?.tracker_values || {},
    // VR (Value Realisation) — internal Wishlink integration. Not shown to brand users.
    // db_ids is an array on the backend but we edit it as a comma-separated string in the
    // UI for simplicity; conversion happens on submit + on initial load below.
    db_ids_text: ((campaign?.db_ids) || []).join(", "),
    vr_sheet_url: campaign?.vr_sheet_url || "",
    vr_status: campaign?.vr_status || "not_generated",
  }));
  const [busy, setBusy] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState(null);

  useEffect(() => {
    (async () => {
      if (campaign?.id) {
        try {
          const { data } = await api.get(`/campaigns/${campaign.id}/config`);
          setTrackerConfig(data.config?.tracker_config || { fields: [] });
        } catch (e) {
          console.error("[CampaignFormModal] failed to load tracker config", e);
        }
      } else {
        // Default tracker fields used by backend
        setTrackerConfig({ fields: [
          { id: "month", name: "Month", type: "text", order: 1 },
          { id: "deliverable", name: "Deliverable", type: "text", order: 2 },
          { id: "brief_link", name: "Brief Link", type: "url", order: 3 },
          { id: "date", name: "Date", type: "date", order: 4 },
          { id: "live_date", name: "Live Date", type: "date", order: 5 },
          { id: "budget", name: "Budget (excl. taxes)", type: "currency", order: 6 },
          { id: "email_subject", name: "Email Subject", type: "text", order: 7 },
          { id: "billing_status", name: "Billing Status", type: "dropdown", options: ["Pending","Paid","Overdue"], order: 8 },
        ]});
      }
    })();
  }, [campaign?.id]);

  // Team picker shows teams from primary brand + every selected partner brand,
  // labelled by which brand each team belongs to so admins can pick deliberately.
  const involvedBrandIds = [form.brand_id, ...(form.partner_brand_ids || [])].filter(Boolean);
  const brandTeams = teams.filter((t) => involvedBrandIds.includes(t.brand_id));
  const brandById = Object.fromEntries(brands.map((b) => [b.id, b]));

  const validate = async () => {
    if (!form.google_sheet_id) return;
    setValidating(true); setValidation(null);
    try {
      const { data } = await api.post("/sheets/validate", {
        sheet_id: form.google_sheet_id,
        tab_map: {
          campaign: form.sheet_tab_campaign,
          creators: form.sheet_tab_creators,
          content: form.sheet_tab_content,
        },
      });
      setValidation(data);
    } catch {
      setValidation({ ok: false, error: "Validation failed" });
    } finally { setValidating(false); }
  };

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      // Strip the UI-only db_ids_text helper and replace with the array the backend expects.
      // Split on commas, trim whitespace, drop empties.
      const { db_ids_text, ...rest } = form;
      const payload = {
        ...rest,
        db_ids: (db_ids_text || "").split(",").map((s) => s.trim()).filter(Boolean),
      };
      if (isEdit) {
        await api.put(`/campaigns/${campaign.id}`, payload);
        toast.success("Campaign updated");
        onSaved();
      } else {
        const { data } = await api.post("/campaigns", payload);
        toast.success("Campaign created");
        // Auto-redirect to configurator if a sheet is connected
        if (form.google_sheet_id && onConfigure) onConfigure(data.campaign?.id);
        else onSaved();
      }
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const setTV = (k, v) => setForm((f) => ({ ...f, tracker_values: { ...f.tracker_values, [k]: v } }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 wl-fade-in" style={{ background: "rgba(26,31,54,0.4)", backdropFilter: "blur(4px)" }} data-testid="campaign-modal">
      <div className="wl-card-solid bg-white w-full max-w-3xl max-h-[92vh] overflow-y-auto wl-scale-in shadow-xl">
        <div className="px-7 py-5 border-b border-[var(--border-default)] sticky top-0 bg-white z-10 flex items-start justify-between">
          <div>
            <div className="font-display text-xl font-semibold">{isEdit ? form.campaign_name : "Create campaign"}</div>
            <div className="text-xs text-[var(--text-secondary)] mt-0.5">
              {isEdit ? <>ID {form.campaign_id} · last edited by {campaign.last_edited_by || "—"} · {timeAgo(campaign.updated_at)} ago</> : "Set up campaign details, sheet connection, and tracker fields."}
            </div>
          </div>
          <button onClick={onClose} className="wl-btn-ghost" data-testid="modal-close"><X size={18} /></button>
        </div>

        <form onSubmit={submit} className="px-7 py-6 space-y-7">
          {/* SECTION A — Campaign details */}
          <Section title="Campaign details" subtitle="Required fields are marked.">
            <Field label="Brand" required>
              <select className="wl-input" value={form.brand_id} onChange={(e) => setForm({ ...form, brand_id: e.target.value, team_ids: [], partner_brand_ids: form.partner_brand_ids.filter((b) => b !== e.target.value) })} required data-testid="field-brand">
                <option value="">Select brand…</option>
                {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </Field>
            <Field label="Partner brands" hint="For cross-brand campaigns (e.g. Brand A × Brand B). The campaign appears on Wishlink Kiwi for every selected brand. Pick teams below from any of these brands.">
              <div className="flex flex-wrap gap-2" data-testid="partner-brands-picker">
                {brands.filter((b) => b.id !== form.brand_id).length === 0 && (
                  <span className="text-xs text-[var(--text-secondary)]">Pick a primary brand first.</span>
                )}
                {brands.filter((b) => b.id !== form.brand_id).map((b) => {
                  const active = form.partner_brand_ids.includes(b.id);
                  return (
                    <button type="button" key={b.id}
                      onClick={() => setForm({
                        ...form,
                        partner_brand_ids: active
                          ? form.partner_brand_ids.filter((x) => x !== b.id)
                          : [...form.partner_brand_ids, b.id],
                        // Drop any team_ids that belonged to this partner if we're removing it
                        team_ids: active
                          ? form.team_ids.filter((tid) => teams.find((t) => t.id === tid)?.brand_id !== b.id)
                          : form.team_ids,
                      })}
                      className={`wl-pill ${active ? "wl-pill-active" : "wl-pill-neutral"}`}
                      data-testid={`partner-brand-${b.id}`}>
                      {b.name}
                    </button>
                  );
                })}
              </div>
            </Field>
            <Field label="Assign to teams">
              <div className="flex flex-wrap gap-2">
                {brandTeams.length === 0 && <span className="text-xs text-[var(--text-secondary)]">No teams in the selected brand(s) yet. Add later.</span>}
                {brandTeams.map((t) => {
                  const active = form.team_ids.includes(t.id);
                  const fromPartner = t.brand_id !== form.brand_id;
                  const teamBrandName = brandById[t.brand_id]?.name;
                  return (
                    <button type="button" key={t.id} onClick={() => setForm({ ...form, team_ids: active ? form.team_ids.filter(x => x !== t.id) : [...form.team_ids, t.id] })}
                      className={`wl-pill ${active ? "wl-pill-active" : "wl-pill-neutral"}`}
                      title={fromPartner ? `Team from partner brand · ${teamBrandName}` : undefined}
                      data-testid={`team-toggle-${t.id}`}>
                      {t.name}{fromPartner ? ` · ${teamBrandName}` : ""}
                    </button>
                  );
                })}
              </div>
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Campaign name" required>
                <input className="wl-input" value={form.campaign_name} onChange={(e) => setForm({ ...form, campaign_name: e.target.value })} required data-testid="field-name" />
              </Field>
              <Field label="Campaign ID" required hint={isEdit ? "Cannot be changed." : "Unique slug. Cannot be changed after creation."}>
                <input className="wl-input" value={form.campaign_id} onChange={(e) => setForm({ ...form, campaign_id: e.target.value })} required disabled={isEdit} data-testid="field-cid" />
              </Field>
              <Field label="Type" hint={TYPES.find(t => t.value === form.campaign_type)?.hint}>
                <select className="wl-input" value={form.campaign_type}
                  onChange={(e) => {
                    const t = e.target.value;
                    // Clear stale fields when switching type so old values don't render in tracker
                    setForm((f) => ({
                      ...f,
                      campaign_type: t,
                      partner_2_name: t === "ecom_x_brand" ? f.partner_2_name : "",
                      partner_2_logo_url: t === "ecom_x_brand" ? f.partner_2_logo_url : "",
                      category_name: t === "ecom_x_category" ? f.category_name : "",
                    }));
                  }}
                  data-testid="field-type">
                  {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </Field>
              <Field label="Status">
                <select className="wl-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="field-status">
                  {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label={form.campaign_type === "brand_only" ? "Brand name" : "Partner 1 name"}><input className="wl-input" value={form.partner_1_name} onChange={(e) => setForm({ ...form, partner_1_name: e.target.value })} data-testid="field-p1-name" /></Field>
              <Field label={form.campaign_type === "brand_only" ? "Brand logo" : "Partner 1 logo"}>
                <LogoUploader value={form.partner_1_logo_url} onChange={(url) => setForm({ ...form, partner_1_logo_url: url })} name={form.partner_1_name || "P1"} testid="p1-uploader" />
              </Field>
              {form.campaign_type === "ecom_x_brand" && <>
                <Field label="Partner 2 name"><input className="wl-input" value={form.partner_2_name} onChange={(e) => setForm({ ...form, partner_2_name: e.target.value })} data-testid="field-p2-name" /></Field>
                <Field label="Partner 2 logo">
                  <LogoUploader value={form.partner_2_logo_url} onChange={(url) => setForm({ ...form, partner_2_logo_url: url })} name={form.partner_2_name || "P2"} testid="p2-uploader" />
                </Field>
              </>}
              {form.campaign_type === "ecom_x_category" && <>
                <Field label="Category name" required hint="Renders next to the partner logo on the tracker (e.g. 'Beauty')">
                  <input className="wl-input" value={form.category_name}
                    onChange={(e) => setForm({ ...form, category_name: e.target.value })}
                    placeholder="e.g. Beauty, Electronics, Apparel"
                    data-testid="field-category" />
                </Field>
                <div /> {/* spacer to keep grid alignment */}
              </>}
              <Field label="Expiry date" hint="Blank = active indefinitely.">
                <input type="date" className="wl-input" value={form.expiry_date || ""} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} data-testid="field-expiry" />
              </Field>
              <Field label="Visible to brand" hint="Off = hidden from brand users.">
                <button type="button" role="switch" aria-checked={form.visible_to_brand}
                  onClick={() => setForm({ ...form, visible_to_brand: !form.visible_to_brand })}
                  className={`relative w-11 h-6 rounded-full transition-colors ${form.visible_to_brand ? "bg-[var(--brand)]" : "bg-gray-300"}`} data-testid="field-visibility">
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${form.visible_to_brand ? "translate-x-5" : ""}`} />
                </button>
              </Field>
            </div>
          </Section>

          {/* SECTION — Internal integration (Value Realisation pipeline) */}
          <Section title="Internal integration" subtitle="Wishlink-only fields. Never shown to brand users.">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Internal DB IDs" hint="Comma-separated, e.g. camp_001, camp_002">
                <input className="wl-input"
                  value={form.db_ids_text}
                  onChange={(e) => setForm({ ...form, db_ids_text: e.target.value })}
                  placeholder="camp_001, camp_002"
                  data-testid="field-db-ids" />
              </Field>
              <Field label="VR status">
                <select className="wl-input" value={form.vr_status}
                  onChange={(e) => setForm({ ...form, vr_status: e.target.value })}
                  data-testid="field-vr-status">
                  {VR_STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
                </select>
              </Field>
              <Field label="VR Sheet URL" hint="Approved Value Realisation sheet. Filled by ops or auto by the VR pipeline.">
                <input className="wl-input"
                  value={form.vr_sheet_url}
                  onChange={(e) => setForm({ ...form, vr_sheet_url: e.target.value })}
                  placeholder="https://docs.google.com/spreadsheets/d/..."
                  data-testid="field-vr-url" />
              </Field>
            </div>
          </Section>

          {/* Tracker dynamic fields */}
          <Section title="Tracker fields" subtitle="Configurable per-campaign in the dashboard configurator.">
            <div className="grid grid-cols-2 gap-4">
              {(trackerConfig.fields || []).sort((a, b) => (a.order || 0) - (b.order || 0)).map((f) => (
                <Field key={f.id} label={f.name}>
                  {f.type === "dropdown" ? (
                    <select className="wl-input" value={form.tracker_values[f.id] || ""} onChange={(e) => setTV(f.id, e.target.value)} data-testid={`tracker-${f.id}`}>
                      <option value="">—</option>
                      {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : f.type === "date" ? (
                    <input type="date" className="wl-input" value={form.tracker_values[f.id] || ""} onChange={(e) => setTV(f.id, e.target.value)} data-testid={`tracker-${f.id}`} />
                  ) : f.type === "currency" || f.type === "number" ? (
                    <input type="number" className="wl-input" value={form.tracker_values[f.id] || ""} onChange={(e) => setTV(f.id, e.target.value)} data-testid={`tracker-${f.id}`} />
                  ) : (
                    <input className="wl-input" value={form.tracker_values[f.id] || ""} onChange={(e) => setTV(f.id, e.target.value)} data-testid={`tracker-${f.id}`} />
                  )}
                </Field>
              ))}
            </div>
          </Section>

          {/* SECTION B — Sheet */}
          <Section title="Tracking data connection" subtitle="Three tabs required. Defaults: campaign / creators / content. Rename if your sheet uses different tab names (e.g. summary / optins).">
            <Field label="Google Sheet URL or ID">
              <div className="flex gap-2">
                <input className="wl-input flex-1" value={form.google_sheet_id} onChange={(e) => { setForm({ ...form, google_sheet_id: e.target.value }); setValidation(null); }} placeholder="https://docs.google.com/..." data-testid="field-sheet" />
                <button type="button" onClick={validate} className="wl-btn-secondary inline-flex items-center gap-2" disabled={!form.google_sheet_id || validating} data-testid="validate-sheet-btn">
                  {validating ? <Loader2 size={14} className="animate-spin" /> : null}
                  Validate sheet
                </button>
              </div>
            </Field>
            <div className="grid grid-cols-3 gap-4">
              <Field label="Campaign tab name" hint="Single-row campaign metrics">
                <input className="wl-input" value={form.sheet_tab_campaign} onChange={(e) => { setForm({ ...form, sheet_tab_campaign: e.target.value }); setValidation(null); }} placeholder="campaign" data-testid="field-tab-campaign" />
              </Field>
              <Field label="Creators tab name" hint="Per-creator rows (e.g. 'optins')">
                <input className="wl-input" value={form.sheet_tab_creators} onChange={(e) => { setForm({ ...form, sheet_tab_creators: e.target.value }); setValidation(null); }} placeholder="creators" data-testid="field-tab-creators" />
              </Field>
              <Field label="Content tab name" hint="Per-post rows">
                <input className="wl-input" value={form.sheet_tab_content} onChange={(e) => { setForm({ ...form, sheet_tab_content: e.target.value }); setValidation(null); }} placeholder="content" data-testid="field-tab-content" />
              </Field>
            </div>
            {validation && (
              <div className={`mt-3 wl-card-solid p-4 text-sm ${validation.ok ? "" : ""}`} data-testid="validation-result">
                <div className="flex items-start gap-2">
                  {validation.ok ? <CheckCircle2 size={16} className="text-emerald-600 mt-0.5" /> : <AlertCircle size={16} className="text-red-600 mt-0.5" />}
                  <div>
                    {validation.ok ? (
                      <>
                        <div className="font-medium">Sheet looks good</div>
                        <div className="mt-1 text-xs text-[var(--text-secondary)]">
                          {Object.entries(validation.details || {}).map(([tab, d]) => (
                            <div key={tab}><strong>{tab}</strong>: {d.row_count} rows · {d.headers.length} headers</div>
                          ))}
                        </div>
                      </>
                    ) : <div className="font-medium text-[#991b1b]">{validation.error}</div>}
                  </div>
                </div>
              </div>
            )}
          </Section>

          <div className="sticky bottom-0 -mx-7 px-7 py-4 bg-white border-t border-[var(--border-default)] flex justify-end gap-2">
            <button type="button" onClick={onClose} className="wl-btn-ghost" data-testid="modal-cancel">Cancel</button>
            <button type="submit" className="wl-btn-primary" disabled={busy} data-testid="modal-save">{busy ? "Saving…" : "Save changes"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <section>
      <div className="mb-4">
        <h3 className="font-display text-base font-semibold">{title}</h3>
        {subtitle && <p className="text-xs text-[var(--text-secondary)] mt-0.5">{subtitle}</p>}
      </div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <label className="block">
      <div className="wl-label mb-1.5">{label}{required && <span className="text-[var(--brand)] ml-1">*</span>}</div>
      {children}
      {hint && <div className="text-[11px] text-[var(--text-secondary)] mt-1">{hint}</div>}
    </label>
  );
}
